/**
 * A visitor who answered the three-question matcher on the extern.com homepage, saw three externships
 * scored from those answers, and pressed one of them. The homepage writes everything this needs onto
 * the sign-up link; this reads it back so sign-up can show the picks beside the form and open the
 * pressed one after the account exists.
 *
 * 🔴 THE SWITCH IS A DEDICATED `handoff` PARAMETER, NOT A UTM. The homepage takes paid traffic, and a
 * matcher link that set its own `utm_*` would overwrite the ad's attribution on the way to sign-up
 * (the leak the portfolio landing pages had). So the homepage passes whatever `utm_*` and click ids it
 * arrived with straight through, and this reads only its own parameter. Read from the URL only, never
 * from anything persisted: a stored value would show "your picks made it" to the same browser on an
 * ordinary sign-up a week later.
 *
 * NOTHING PERSONAL GOES IN THESE PARAMETERS. The answers travel as short codes from the fixed lists
 * below, the externships as our own slugs. No name, no email, no free text.
 *
 * INERT FOR ORDINARY SIGN-UP TRAFFIC. Without `handoff=homepage_match` AND a program slug we know, this is
 * null and sign-up renders byte-for-byte as it ships.
 *
 * The other side of the contract is the matcher in the homepage repo (`homepage-lp/index.html`):
 * its option lists and slugs must stay in step with the vocabularies here.
 */

/** The value of `handoff` that names this arrival. */
export const MATCHER_ARRIVAL_HANDOFF = 'homepage_match';

export const MATCHER_ARRIVAL_PARAMS = {
  handoff: 'handoff',
  focus: 'focus',
  goal: 'goal',
  picks: 'picks',
  program: 'program',
  stage: 'stage',
} as const;

/** "What kind of work do you want to get your hands on?" Up to three. */
export const MATCHER_FOCUS_LABELS = {
  ai: 'AI & Technology',
  consulting: 'Strategy & Consulting',
  creative: 'Creative Arts & Media',
  data: 'Data Analytics & Visualization',
  finance: 'Finance',
  marketing: 'Marketing & Sales',
  product: 'Product Management & Design',
} as const;

/** "What do you want to walk away with?" Up to three. */
export const MATCHER_GOAL_LABELS = {
  certificate: 'A professional certificate',
  feedback: 'Structured feedback on my work',
  interviews: 'Real work I can talk about in interviews',
  names: 'Recognizable company names on my resume',
  newfield: 'A first look at a new field',
  skill: 'One specific skill, practiced for real',
} as const;

/** "Where are you right now?" One. */
export const MATCHER_STAGE_LABELS = {
  early: 'Some experience, but my career hasn’t really started',
  established: 'Firmly established in my career',
  returning: 'Established, coming back after a break',
  starting: 'Little work experience so far',
  switching: 'Started my career, thinking about a change',
} as const;

/** The externships the homepage matcher can score. Content for each lives with the arrival panel. */
export const MATCHER_PROGRAM_SLUGS = [
  'amazon',
  'beats',
  'bereal',
  'breaking',
  'canva',
  'hydroficient',
  'imh',
  'pfizer',
  'pinterest',
  'tiktok',
  'trubridge',
  'yinan',
] as const;

export type MatcherFocus = keyof typeof MATCHER_FOCUS_LABELS;
export type MatcherGoal = keyof typeof MATCHER_GOAL_LABELS;
export type MatcherStage = keyof typeof MATCHER_STAGE_LABELS;
export type MatcherProgramSlug = (typeof MATCHER_PROGRAM_SLUGS)[number];

export interface MatcherArrival {
  /** Fields they picked, in the order they picked them. Empty when the link carried none we know. */
  focus: MatcherFocus[];
  /** What they want to walk away with, same rules. */
  goal: MatcherGoal[];
  /** The scored externships, the pressed one FIRST, then the others. One to three, no repeats. */
  picks: MatcherProgramSlug[];
  /** The externship they pressed. */
  program: MatcherProgramSlug;
  /** Where they are in their career, or null when the link carried nothing we know. */
  stage: MatcherStage | null;
}

/** Each answer list is capped where the matcher caps it. */
const MAX_LIST_ANSWERS = 3;
const MAX_PICKS = 3;
const LIST_SEPARATOR = ',';

function isOneOf<T extends string>(allowed: readonly T[], value: string): value is T {
  return (allowed as readonly string[]).includes(value);
}

/**
 * The known, distinct values of a comma-separated list, in order, capped. Unknown values are dropped
 * rather than failing the arrival: a list the homepage adds an option to tomorrow must not send today's
 * visitors back to the plain form.
 */
function parseList<T extends string>(raw: string | null, allowed: readonly T[], max: number): T[] {
  if (!raw) {
    return [];
  }

  const seen = new Set<T>();
  for (const part of raw.split(LIST_SEPARATOR)) {
    const value = part.trim();
    if (isOneOf(allowed, value)) {
      seen.add(value);
    }
    if (seen.size === max) {
      break;
    }
  }
  return [...seen];
}

export function parseMatcherArrival(searchParams: Pick<URLSearchParams, 'get'> | null): MatcherArrival | null {
  if (searchParams?.get(MATCHER_ARRIVAL_PARAMS.handoff) !== MATCHER_ARRIVAL_HANDOFF) {
    return null;
  }

  const program = searchParams.get(MATCHER_ARRIVAL_PARAMS.program)?.trim() ?? '';
  if (!isOneOf(MATCHER_PROGRAM_SLUGS, program)) {
    return null;
  }

  const others = parseList(searchParams.get(MATCHER_ARRIVAL_PARAMS.picks), MATCHER_PROGRAM_SLUGS, MAX_PICKS).filter(
    (slug) => slug !== program,
  );

  return {
    focus: parseList(
      searchParams.get(MATCHER_ARRIVAL_PARAMS.focus),
      Object.keys(MATCHER_FOCUS_LABELS) as MatcherFocus[],
      MAX_LIST_ANSWERS,
    ),
    goal: parseList(
      searchParams.get(MATCHER_ARRIVAL_PARAMS.goal),
      Object.keys(MATCHER_GOAL_LABELS) as MatcherGoal[],
      MAX_LIST_ANSWERS,
    ),
    picks: [program, ...others].slice(0, MAX_PICKS),
    program,
    stage:
      parseList(
        searchParams.get(MATCHER_ARRIVAL_PARAMS.stage),
        Object.keys(MATCHER_STAGE_LABELS) as MatcherStage[],
        1,
      )[0] ?? null,
  };
}
